import { computed, inject, Injectable } from '@angular/core';
import { BasePromo, EventType } from '../base-promo';
import { TournamentsService } from '../../tournaments/tournaments.service';
import { GameListConfig } from '../../../core/shared/components/game-list/game-list.component';
import { combineLatest, concatMap, Observable, of, Subject } from 'rxjs';
import { ITournament } from '../../tournaments/tournaments.interface';
import { useStore } from 'ngx-unificator/store';
import { GlobalStoreName } from '../../../core/store';
import {
  SlotPrizeItemType,
  SummerPageResponse,
  SummerStoreEntity,
} from '../../../core/store/summer.store';
import { filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { PaymentMode } from '../../../core/services/user/user-payment-mode.service';
import { BonusStage } from '../../../core/services/user/data/user-bonuses.data';
import { GameCategory } from '../../../core/services/games/game-category';
import { HttpStatus } from '../../../core/services/response-handler.service';

export const ALREADY_SPINED_SLOT_STR = 'already_spined_slot';

export const BONUS_IDS: string[] = [
  'promo_spins_1_sp25',
  'promo_spins_2_sp25',
  'promo_spins_3_sp25',
];

export enum SummerAction {
  GO_TO_DEPOSIT = 'deposit',
  TOURNAMENTS = 'tournaments',
  PROMO_AUTH = 'promo-auth',
  GET_BONUS = 'get-bonus',
  GO_TO_BONUSES = 'bonuses',
  GO_TO_TELEGRAM = 'telegram',
  GO_TO_BONUS_SHOP = 'shop',
}

@Injectable({
  providedIn: 'root',
})
export class SummerService extends BasePromo {

  /**
   * Event name
   */
  public eventName = 'summer';

  /**
   * Services
   */
  public tournaments: TournamentsService = inject(TournamentsService);

  /**
   * Game list config
   */
  public gamesListConfig: GameListConfig = {
    filter: {
      category: GameCategory.SUMMER,
      limit: 5,
    },
    link: '/games/summer-favorites',
    title: '',
    hideTitle: true,
  };

  /**
   * Tournaments list
   */
  public tournaments$: Observable<ITournament[]> = this._getTournaments$();

  /**
   * Summer promo store
   */
  public summerStore =
    useStore<SummerStoreEntity>(GlobalStoreName.SUMMER_STORE);

  /**
   * Summer promo getters
   */
  public store = computed(() => this.summerStore.storeSignal());
  public slots = computed(() => this.summerStore.storeSignal().slots);

  constructor() {
    super();
  }

  /**
   * Init promotion and reduce store items
   */
  public initPromo$() {
    this._updateLoadingPromo(true);
    return this.paymentMode.currentMode$.pipe(
      concatMap(mode => this._fetchPromoData$(mode)),
      filter(data => !!data[0]),
      filter((data) => !this._isPromoError(data[0])),
      tap(([promo, lootboxes, freespins, bonuses]) => {
        this._updatePageStore(promo);
        this._updateCurrentBonus(lootboxes, freespins, bonuses);
        this._updateParticipateInPromo(lootboxes);
        this._updateLoadingPromo(false);
        this.initWelcomePromoModal();
      }),
    );
  }

  /**
   * Activate bonus by type of bonus
   */
  public onActivateBonus$(): Observable<any> {
    const { promoIssuedLootbox, welcomeLootbox, fs, cash } = this.store().currentBonus || {};

    const cashPrize = cash ? `${cash.amount} ${cash.currency}` : undefined;

    if (this.store().isUserCanPartiallyParticipateInPromo) {
      if (welcomeLootbox) {
        return this.lootboxService.activateLootBox(welcomeLootbox.id).pipe(
          switchMap((response) => {
            const fs_count = this.bonuses.resolveBonusAttributes(
              response?.bonuses[0].attributes)?.freespins_count;
            this._updateCurrentPrize(fs_count, cashPrize ?? undefined);
            const currentFsBonus = this.bonuses.mapBonusTitle(response?.bonuses[0]?.title);
            return this.bonuses.freeSpinsList().pipe(
              switchMap((list) => {
                const foundFs = list.find((e) => e.title === currentFsBonus);
                return this.bonuses.activateFreeSpins(foundFs?.id);
              }),
            );
          }),
        );
      } else if (fs) {
        return this.bonuses.activateFreeSpins(fs?.id).pipe(
          tap(() => {
            this._updateCurrentPrize(fs?.freespins_total, cashPrize ??
              undefined);
          }),
        );
      }
    }

    if (this.store().isUserCanParticipateInPromo && promoIssuedLootbox) {
      return this.lootboxService.activateLootBox(promoIssuedLootbox?.id).pipe(
        switchMap((response) => {
          const fs_count = this.bonuses.resolveBonusAttributes(
            response?.bonuses[0].attributes)?.freespins_count;
          this._updateCurrentPrize(fs_count);
          const currentFsBonus = this.bonuses.mapBonusTitle(response?.bonuses[0]?.title);
          return this.bonuses.freeSpinsList().pipe(
            switchMap((list) => {
              const foundFs = list.find((e) => e.title === currentFsBonus);
              return this.bonuses.activateFreeSpins(foundFs?.id);
            }),
          );
        }),
      );
    }
  }

  /**
   * Action rotes config
   */
  public actionRoutesConfig = {
    [SummerAction.GET_BONUS]: {
      key: null,
      url: '/account',
      authUrl: ['account'],
    },
    [SummerAction.GO_TO_BONUSES]: {
      key: null,
      url: '/bonuses',
      authUrl: ['bonuses'],
    },
    [SummerAction.TOURNAMENTS]: {
      key: 'promo-tournaments',
      url: '/tournaments',
      authUrl: ['tournaments'],
    },
    [SummerAction.GO_TO_DEPOSIT]: {
      key: 'promo-deposit',
      url: '/account/deposit',
      authUrl: ['account', 'deposit'],
    },
    [SummerAction.PROMO_AUTH]: {
      key: 'promo-auth',
      url: '/promo/summer',
      authUrl: null,
    },
    [SummerAction.GO_TO_TELEGRAM]: {
      key: null,
      url: 'https://t.me/Official21Bit',
      authUrl: null,
      nativeUrl: true,
    },
    [SummerAction.GO_TO_BONUS_SHOP]: {
      key: null,
      url: '/bonus-shop',
      authUrl: null,
    },
  };

  public readQueryParamsDepositSuccess(destroy$: Subject<void>) {
    this.route.queryParams.pipe(
      filter(({ deposit_success }) => !!deposit_success),
      tap(() => this.cookie.delete(ALREADY_SPINED_SLOT_STR)),
      takeUntil(destroy$),
    ).subscribe();
  }

  /**
   * Update spinning state slots
   * @param isSpinFinished
   */
  public updateSpinSlots(isSpinFinished: boolean) {
    this.summerStore.storeSignal.update(store => {
      return {
        ...store,
        isSpinFinished,
      };
    });
  }

  public updateDisableSpin(isDisableSpin: boolean) {
    this.summerStore.storeSignal.update(store => {
      return {
        ...store,
        isDisableSpin,
      };
    });
  }

  public clearPrizeStore() {
    this.summerStore.storeSignal.update(store => {
      return {
        ...store,
        currentPrize: null,
      };
    });
  }

  /**
   * Fetch necessary data for promo
   * @param mode
   * @private
   */
  private _fetchPromoData$(mode: string): Observable<[SummerPageResponse, any[], any[], any[]]> {
    return combineLatest([
      this.getPage(mode === PaymentMode.CRYPTO ?
        `${this.eventName}-crypto-25` : `${this.eventName}-fiat-25`),
      this.user.auth ? this.bonuses.lootBoxList() : of([]),
      this.user.auth ? this.bonuses.freeSpinsList() : of([]),
      this.user.auth ? this.bonuses.bonusList() : of([]),
    ]);
  }


  /**
   * Update current prize store
   * @param fs_count
   * @param cash
   * @private
   */
  private _updateCurrentPrize(fs_count: number, cash?: string) {
    this.summerStore.storeSignal.update(store => ({
      ...store,
      currentPrize: {
        cash,
        fs_count,
      },
    }));
    this.cookie.set(ALREADY_SPINED_SLOT_STR, '1');
  }

  /**
   * Update page store
   * @param promo
   * @private
   */
  private _updatePageStore(promo: SummerPageResponse): any {
    if (this._isPromoError(promo)) return;

    const prizes = [
      ...Object.values(promo[0]?.Prizes).map((prize: any) => ({
        ...prize,
        fs: Number(prize.fs),
        image: `/assets/img/promo/summer/prizes/${Number(prize.image)}.png`,
      })),
    ];

    this.summerStore.storeSignal.update(store => {
      return {
        ...store,
        page: {
          ...promo?.map(promoItem => ({
            ...promoItem,
            HowItWorksSteps: [...Object.values(promoItem?.HowItWorksSteps)],
            unpublishAt: this.time.toLocalDate(promoItem?.unpublishAt),
          }))[0],
        },
        slots: {
          prizes,
          initialPrizes: prizes.slice(0, 3),
        },
      };
    });
  };

  private _isPromoError(promo: SummerPageResponse): promo is { statusCode: HttpStatus } {
    return typeof promo === 'object' &&
      'statusCode' in promo &&
      (promo.statusCode === HttpStatus.MOVED_PERMANENTLY ||
        promo.statusCode === HttpStatus.NOT_FOUND);
  }

  /**
   * Update current bonus store
   * @param lootboxes
   * @param freespins
   * @param bonuses
   * @private
   */
  private _updateCurrentBonus(lootboxes: any, freespins: any, bonuses: any) {

    const cash = this._getFilteredCashWelcomeBonuses(bonuses);
    const fs = this._getFilteredFreespinsWelcomeBonuses(freespins);
    const welcomeLootbox = this._getFilteredWelcomeLootbox(lootboxes);
    const promoIssuedLootbox = this._getFilteredPromoLootbox(lootboxes, BonusStage.ISSUED);

    this.summerStore.storeSignal.update(store => {
      return {
        ...store,
        currentBonus: fs && cash || welcomeLootbox && cash || fs || promoIssuedLootbox ? {
          cash,
          fs,
          welcomeLootbox,
          promoIssuedLootbox,
        } : null,
      };
    });
  }

  /**
   * Update participating in promo store items
   * @private
   */
  private _updateParticipateInPromo(lootboxes: any) {

    const promoIssuedLootbox = this.store().currentBonus?.promoIssuedLootbox;
    const promoActivatedLootbox = this._getFilteredPromoLootbox(lootboxes, BonusStage.ACTIVATED);
    const cash = this.store().currentBonus?.cash;
    const fs = this.store().currentBonus?.fs;
    const welcomeLootbox = this.store().currentBonus?.welcomeLootbox;

    const isUserAlreadyActivatedPromoLootbox = this._checkActivatedTodayLootbox(promoActivatedLootbox);
    const isAllWelcomePackClaimed = this.group.isExistGroup('ID407');
    const isUserCanParticipateInPromo = Boolean(isAllWelcomePackClaimed && promoIssuedLootbox);
    const isUserCanPartiallyParticipateInPromo = Boolean((cash || fs || welcomeLootbox) && !promoIssuedLootbox);

    this.summerStore.storeSignal.update(store => {
      return {
        ...store,
        isUserCanParticipateInPromo,
        isUserCanPartiallyParticipateInPromo,
        isAllWelcomePackClaimed,
        isUserAlreadyActivatedPromoLootbox,
      };
    });
  }

  /**
   * Update loading promo state
   * @param isLoading
   * @private
   */
  private _updateLoadingPromo(isLoading: boolean) {
    this.summerStore.storeSignal.update(store => {
      return {
        ...store,
        isLoadedPromo: !isLoading,
      };
    });
  }

  /**
   * Get filtered cash bonuses
   * @param arrayToCheck
   * @private
   */

  private _getFilteredCashWelcomeBonuses(arrayToCheck: any[]): any {
    return arrayToCheck?.filter(bonus =>
      bonus.stage === BonusStage.HANDLE_BETS &&
      bonus.isWelcome && !this.cookie.check(ALREADY_SPINED_SLOT_STR))
      .sort((a, b) => b.created_at - a.created_at)[0];
  }

  /**
   * Get filtered welcome freespins
   * @param arrayToCheck
   * @private
   */
  private _getFilteredFreespinsWelcomeBonuses(arrayToCheck: any[]): any {
    return arrayToCheck?.filter(bonus => bonus.stage === BonusStage.ISSUED &&
      bonus.isWelcome && !this.cookie.check(ALREADY_SPINED_SLOT_STR))
      .sort((a, b) => b.created_at - a.created_at)[0];
  }

  /**
   * Get filtered welcome lootbox
   * @param arrayToCheck
   * @private
   */
  private _getFilteredWelcomeLootbox(arrayToCheck: any[]): any {
    return arrayToCheck?.filter(lootbox => lootbox.isWelcome && lootbox.stage === BonusStage.ISSUED)[0];
  }

  /**
   * Check if user already activated promo lootbox
   * @param promoLootbox
   * @private
   */
  private _checkActivatedTodayLootbox(promoLootbox: any): boolean {
    return promoLootbox?.stage === BonusStage.ACTIVATED &&
      BONUS_IDS.includes(promoLootbox.e.title) && this.time.isTodayDate(promoLootbox?.created_at);
  }

  /**
   * Get filtered last promo lootbox
   * @param arrayToCheck
   * @param stage
   * @private
   */
  private _getFilteredPromoLootbox(arrayToCheck: any[], stage: BonusStage): any {
    return arrayToCheck?.filter(lootbox =>
      BONUS_IDS.includes(lootbox.e.title) && lootbox.e.stage === stage)
      .sort((a, b) => b.e.created_at - a.e.created_at)[0];
  }

  /**
   * Get promo tournaments
   * @private
   */
  private _getTournaments$(): Observable<any> {
    return this.tournaments.list({ with_game_list: 1 }).pipe(
      filter((tournaments) => !!tournaments),
      map(tournaments => tournaments.now.filter(tournament => {
        return tournament.slug.includes('easter');
      })),
    );
  }

  /**
   * Update sequence of prizes
   * @param i
   */
  public updateSlotSequence(i: number): void {
    this.summerStore.storeSignal.update(store => {
      return {
        ...store,
        slots: {
          ...store.slots,
          sequencePrizes: this._generatePrizeSequence(
            store.slots.initialPrizes[i],
            store.slots.finalPrize),
        },
      };
    });
  }

  /**
   * Update final prize
   * @param fsCount
   */
  public updateFinalSlotPrize(fsCount: number): void {
    const findIndex = this.slots().prizes.findIndex(item => item.fs === fsCount);
    this.summerStore.storeSignal.update(store => {
      return {
        ...store,
        slots: {
          ...store.slots,
          finalPrize: store.slots.prizes[findIndex],
        },
      };
    });
  }

  /**
   * Update current prizes
   * @param initialPrizes
   * @private
   */
  public updateInitialSlotPrizes(initialPrizes: SlotPrizeItemType[]): void {
    this.summerStore.storeSignal.update(store => {
      return {
        ...store,
        slots: {
          ...store.slots,
          initialPrizes,
        },
      };
    });
  }

  /**
   * Generate sequence of prizes
   * @param basePrize
   * @param finalPrize
   * @private
   */
  private _generatePrizeSequence(basePrize: SlotPrizeItemType, finalPrize: SlotPrizeItemType): SlotPrizeItemType[] {
    return Array(20).fill(basePrize).concat(finalPrize);
  }

  async checkActivatedBonuses() {
    const component = await import('../summer/summer-welcome-modal/summer-welcome-modal.component');
    if (!this.store().isUserAlreadyActivatedPromoLootbox) {
      await this.modals.openLazy(component?.SummerWelcomeModalComponent);
      const now = new Date();
      const day = now.getDay();
      const diff = 7 - day;
      const endOfWeek = new Date(now);
      endOfWeek.setDate(now.getDate() + diff);
      endOfWeek.setHours(23, 59, 59, 999);
      this.cookie.set('welcome-modal-promo', 'true', endOfWeek, '/');
    }
    return of(true);
  }

  openHuntModal(type: EventType) {
  }
}
